import clsx from "clsx"
import { createContext, useEffect, useMemo, useRef, useState } from "react"

import styles from "./TabStack.module.scss"
import { TabStackContextValues, TabStackProps } from "./TabStack.types"

export const TabStackContext = createContext<TabStackContextValues | undefined>(undefined)
TabStackContext.displayName = "TabStack"

export default function TabStack(props: TabStackProps) {
    const { children, variant, size, isFullWidth = false } = props

    const containerRef = useRef<HTMLDivElement | null>(null)
    const [indicatorStyle, setIndicatorStyle] = useState<{ width: number; left: number } | null>(null)
    const [highlightStyle, setHighlightStyle] = useState<{
        width: number
        left: number
        top: number
        height: number
    } | null>(null)

    const hasUnderline = useMemo(() => variant === "underlined", [variant])

    useEffect(() => {
        const container = containerRef.current
        if (!container) {
            return
        }

        const recalc = () => {
            const selected = container.querySelector<HTMLButtonElement>('[role="tab"][aria-selected="true"]')
            if (!selected) {
                setIndicatorStyle(null)
                setHighlightStyle(null)
                return
            }
            const rect = selected.getBoundingClientRect()
            const parentRect = container.getBoundingClientRect()
            const left = rect.left - parentRect.left + container.scrollLeft
            const top = rect.top - parentRect.top + container.scrollTop
            const width = rect.width
            const height = rect.height

            if (hasUnderline) {
                setIndicatorStyle({ width, left })
                setHighlightStyle(null)
            } else {
                setIndicatorStyle(null)
                setHighlightStyle({ left, width, top, height })
            }
        }

        recalc()

        const resizeObserver = new ResizeObserver(recalc)
        resizeObserver.observe(container)
        Array.from(container.querySelectorAll('[role="tab"]')).forEach((el) => resizeObserver.observe(el))

        const mutationObserver = new MutationObserver(recalc)
        mutationObserver.observe(container, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["aria-selected", "style", "class"],
        })

        const onScroll = () => recalc()
        container.addEventListener("scroll", onScroll, { passive: true })

        window.addEventListener("resize", recalc)

        return () => {
            resizeObserver.disconnect()
            mutationObserver.disconnect()
            container.removeEventListener("scroll", onScroll)
            window.removeEventListener("resize", recalc)
        }
    }, [children, hasUnderline, size])

    return (
        <div
            ref={containerRef}
            className={clsx(styles.base, styles[`${size}Size`], {
                [styles.contained]: variant === "contained",
                [styles.underlined]: variant === "underlined",
                [styles.fullWidth]: isFullWidth,
            })}
            role="tablist"
        >
            {!hasUnderline && highlightStyle && (
                <div
                    aria-hidden={true}
                    className={clsx(styles.highlightIndicator, {
                        [styles.highlightContained]: variant === "contained",
                        [styles.highlightDefault]: variant === "default",
                    })}
                    style={{
                        top: `${highlightStyle.top}px`,
                        height: `${highlightStyle.height}px`,
                        width: `${highlightStyle.width}px`,
                        left: `${highlightStyle.left}px`,
                    }}
                />
            )}
            <TabStackContext.Provider value={{ variant, size }}>{children}</TabStackContext.Provider>
            {hasUnderline && indicatorStyle && (
                <div
                    aria-hidden={true}
                    className={styles.underlineIndicator}
                    style={{ width: `${indicatorStyle.width}px`, left: `${indicatorStyle.left}px` }}
                />
            )}
        </div>
    )
}
